'use client';

import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { http } from '../../../lib/api/http';
import { V2, V4 } from '../../../lib/api/urls';
import { eventStatuses } from '../../../constants/events';
import { Container } from '../../layout/Container';
import {
  catalogOrderingUrlToApi,
  DEVICE_SCREENS_MAX_STRING,
  DEVICE_SCREENS_MIN_STRING,
} from '../../../lib/consts';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import { ShareBlock } from '../../blocks/ShareBlock';
import { Description } from '../../blocks/Description';
import { CommentsBlock } from '../../comments/CommentsBlock';
import { Subscription } from '../../about/Subscription';
import { MobileApps } from '../../about/MobileApps';
import { PlaceBanner } from '../../blocks/PlaceBanner';
import LocationSVG from '../../../public/img/location.svg';
import { ReadMore } from '../../blocks/ReadMore';
import { PictureModal } from '../../picture/PictureModal';
import { Viewed } from '../../viewed/viewed';
import { fetchViewed } from '../../../utils/viewed';
import { YaMapModal } from '../../ya-map/YaMapModal';
import { useModal } from '../../../providers/modal';
import { Posts } from '../../posts/posts';
import { ImagesSlider } from '../../actions/images-slider';
import { PeopleSlider } from '../../person/people-slider';
import { Action, City, Place } from '../../../models';
import { PersonInListV3 } from '../../../models/person';
import {
  setLocalStorageItem,
  getLocalStorageItem,
} from '../../../lib/api/browserStorage';
import { getEvents } from '../../../lib/services/backend';

export function PlacePage({
  currentCity,
  placeData,
  persons,
  gorbiletRekomenduet,
}: {
  currentCity: City;
  placeData: Place;
  persons: { results: PersonInListV3[] };
  gorbiletRekomenduet: { results: Action[] };
}) {
  const theme = useTheme();
  const [imageModal, setImageModal] = useState<{
    slide: number | null;
    visible: boolean;
  }>({ slide: null, visible: false });
  const [viewed, setViewed] = useState<{ results: Action[] }>({ results: [] });
  const [relatedEvents, setRelatedEvents] = useState<{ results: Action[] }>({
    results: [],
  });

  const modalState = useModal();
  useEffect(() => {
    http.post(V2.eventViews, {
      Place: placeData.remote_id,
    });
  }, [placeData.remote_id]);

  useEffect(() => {
    async function fetch() {
      const viewedList = await fetchViewed();
      setViewed(viewedList);
    }
    fetch();
    let viewedActions = [];
    viewedActions =
      (JSON.parse(getLocalStorageItem('viewed-events') || '[]') || []).slice(
        0,
        8
      ) || [];
    setLocalStorageItem('viewed-events', JSON.stringify([...viewedActions]));

    function getRelatedEvents() {
      return getEvents({
        places: placeData.id,
        city: undefined,
        is_web: true,
        status: eventStatuses.active,
        ordering: catalogOrderingUrlToApi.popular,
      });
    }
    getRelatedEvents().then((events) => setRelatedEvents(events));
  }, [placeData.id]);

  return (
    <>
      <Container
        // className='container'
        css={{
          padding: '16px 8px 0 8px',
          [DEVICE_SCREENS_MIN_STRING.xxl]: {
            maxWidth: '1280px !important',
          },
          [DEVICE_SCREENS_MAX_STRING.md]: {
            padding: 0,
          },
        }}
      >
        <div
          css={{
            marginBottom: 16,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginBottom: 0,
            },
          }}
        >
          <BreadCrumbs
            currentCity={currentCity}
            items={[
              {
                title: 'Каталог мест',
                url: 'place-catalog',
              },
              {
                title: placeData.title,
              },
            ]}
          />
        </div>
      </Container>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <Container
          className='container'
          css={{
            padding: '0 8px',
            [DEVICE_SCREENS_MIN_STRING.xxl]: {
              maxWidth: '1280px !important',
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              padding: '0 !important',
            },
          }}
        >
          <PlaceBanner item={placeData} />
        </Container>
        <Container
          className='container'
          css={{
            [DEVICE_SCREENS_MIN_STRING.xxl]: {
              maxWidth: '1280px !important',
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              padding: '0 !important',
            },
          }}
        >
          {relatedEvents?.results.length > 0 && (
            <div
              css={{
                marginTop: 10,
                padding: '0 8px',
                [DEVICE_SCREENS_MIN_STRING.md]: {
                  display: 'none',
                },
              }}
            >
              <Posts title='Мероприятия' href={null} actions={relatedEvents} />
            </div>
          )}
          <div
            itemScope
            itemType='https://schema.org/EventVenue'
            css={{
              display: 'flex',
              marginTop: 30,
              marginBottom: 50,
              [DEVICE_SCREENS_MAX_STRING.md]: {
                flexDirection: 'column',
                marginTop: 0,
                padding: 8,
              },
            }}
          >
            <span css={{ display: 'none' }} itemProp='name'>
              {placeData.title}
            </span>
            <span css={{ display: 'none' }} itemProp='telephone'>
              {placeData.phone_number}
            </span>
            <div className='col-12 col-md-5 col-lg-4 col-xl-3'>
              <div css={{ marginBottom: 15 }}>
                <ShareBlock item={placeData} />
              </div>
              {placeData.addresses.length > 0 && (
                <div
                  css={{
                    backgroundColor: theme.colors.white,
                    borderRadius: 4,
                    padding: 30,
                    marginBottom: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    fontFamily: 'Golos',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    letterSpacing: '0em',
                    color: '#333',
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      padding: 20,
                    },
                  }}
                >
                  <div
                    css={{
                      display: 'flex',
                      gap: 8,
                      fontFamily: 'Montserrat',
                      color: theme.colors.red,
                      fontSize: 12,
                    }}
                  >
                    <LocationSVG css={{ width: 16, fill: theme.colors.red }} />
                    <p>Где</p>
                  </div>
                  <div>
                    {placeData.addresses.map((address) => (
                      <div itemProp='address' key={address}>
                        {address}
                      </div>
                    ))}
                    {placeData.coordinates_list &&
                      placeData.coordinates_list[0] && (
                        <button
                          key={placeData.coordinates_list[0]}
                          css={{
                            fontFamily: 'Golos',
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: '20px',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            borderBottom: '1px dashed rgba(0, 0, 0, 0.5)',
                            color: theme.colors.black,
                            ':hover': {
                              borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
                            },
                          }}
                          type='button'
                          onClick={() => {
                            if (modalState) {
                              modalState.setModalState((oldState) => ({
                                ...oldState,
                                show: true,
                                content: (
                                  <YaMapModal
                                    cords={placeData.coordinates_list[0]}
                                    setModalState={modalState.setModalState}
                                  />
                                ),
                                options: { width: 800 },
                              }));
                            }
                          }}
                        >
                          Показать на карте
                        </button>
                      )}
                  </div>
                </div>
              )}
            </div>
            <div
              className='col-12 col-md-6 push-md-1 col-lg-7 push-lg-1'
              css={{
                position: 'relative',
                left: '8.33333333%',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  left: 0,
                },
              }}
            >
              <div css={{ marginBottom: 40 }}>
                <Description title='' description={placeData.description} />
                <div
                  css={{ display: 'none' }}
                  itemProp='description'
                  dangerouslySetInnerHTML={{
                    __html: `${placeData.description.slice(0, 999)}...`,
                  }}
                />
              </div>
              {(placeData.images.length > 1 || placeData.video_url) && (
                <ImagesSlider
                  videoInfo={{
                    video: placeData.video_url,
                    cover: placeData.video_cover,
                  }}
                  images={placeData.images}
                  onSlideClick={(index) =>
                    setImageModal({ slide: index, visible: true })
                  }
                />
              )}
              {!!persons?.results.length && (
                <div css={{ marginBottom: 30 }}>
                  <div
                    css={{
                      fontFamily: 'Montserrat',
                      fontSize: 13,
                      fontWeight: 500,
                      lineHeight: '16px',
                      letterSpacing: '0.04em',
                      color: theme.colors.red,
                      textTransform: 'uppercase',
                      marginBottom: 10,
                    }}
                  >
                    Персоны
                  </div>
                  <PeopleSlider
                    isShowArrows={persons.results.length > 3}
                    personList={persons.results}
                  />
                </div>
              )}
              <div>
                <CommentsBlock
                  currentCity={currentCity}
                  contentType='events.place'
                  eventId={placeData.id}
                />
              </div>
            </div>
          </div>
          <div
            css={{
              marginTop: '7rem',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                padding: '0 8px',
              },
            }}
          >
            <div
              css={{
                [DEVICE_SCREENS_MIN_STRING.xxl]: {
                  margin: '0 -18px',
                },
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  margin: '0 -10.5px',
                },
              }}
            >
              {!!placeData.materials.length && (
                <ReadMore
                  person={null}
                  materials={placeData.materials}
                  place={placeData}
                />
              )}
            </div>
            {relatedEvents?.results.length > 0 && (
              <div
                css={{
                  marginTop: '4rem',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    display: 'none',
                  },
                }}
              >
                <Posts
                  title='Мероприятия'
                  href={null}
                  actions={relatedEvents}
                />
              </div>
            )}
            <Viewed title='Горбилет рекомендует' viewed={gorbiletRekomenduet} />
            <Viewed title='Вы смотрели' viewed={viewed} />
          </div>

          <div
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: '4rem',
              marginTop: '2rem',
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                gridTemplateColumns: '1fr',
              },
              [DEVICE_SCREENS_MAX_STRING.md]: {
                padding: '0 8px',
              },
            }}
          >
            <Subscription currentCity={currentCity} />
            <MobileApps />
          </div>
          {imageModal.visible &&
            (!!placeData.images?.length || !!placeData.video_url) && (
              <PictureModal
                videoInfo={{
                  video: placeData.video_url,
                  cover: placeData.video_cover,
                }}
                images={placeData.images.slice(1)}
                imageModal={imageModal}
                scrollY={window.scrollY}
                setVisible={setImageModal}
              />
            )}
        </Container>
      </main>
    </>
  );
}
